import { create } from 'zustand';

import type { WhatsNewDataItem } from '@/features/userManual/components/WhatsNew/types';

const WHATS_NEW_STORAGE_KEY = 'whatsNew';

type WhatsNewInfo = {
    email: string;
    version: string;
    showNotification: boolean;
};

export type WhatsNewState = {
    showNotification: boolean;
    showBottomSheet: boolean;
    initializeWhatsNewStatus: (email: string | undefined, filteredData: WhatsNewDataItem[]) => void;
    dismissNotification: (email: string | undefined, version: string) => void;
    dismissBottomSheet: () => void;
    filteredWhatsNewData: WhatsNewDataItem[];
    setFilteredWhatsNewData: (data: WhatsNewDataItem[]) => void;
};

const readLocalStorage = (): WhatsNewInfo | null => {
    try {
        const item = localStorage.getItem(WHATS_NEW_STORAGE_KEY);
        return item ? JSON.parse(item) : null;
    } catch {
        return null;
    }
};

const writeLocalStorage = (data: WhatsNewInfo) => {
    localStorage.setItem(WHATS_NEW_STORAGE_KEY, JSON.stringify(data));
};

export const useWhatsNewStore = create<WhatsNewState>(set => ({
    showNotification: false,
    showBottomSheet: false,
    filteredWhatsNewData: [],
    setFilteredWhatsNewData: (data: WhatsNewDataItem[]) => set({ filteredWhatsNewData: data }),

    /**
     * Checks if the user has already seen the latest "What's New" version and the bottom sheet.
     * If not, it saves it to localStorage and shows the bottom sheet and the notification badge.
     */
    initializeWhatsNewStatus: (email, filteredData) => {
        if (!email || filteredData.length === 0) {
            set({ showNotification: false, showBottomSheet: false });
            return;
        }

        const latestItem = filteredData[0];
        const stored = readLocalStorage();

        const alreadySeenLatest = stored?.email === email && stored?.version === latestItem.version;

        if (alreadySeenLatest) {
            set({
                showNotification: !!stored?.showNotification,
                showBottomSheet: false,
            });
            return;
        }

        writeLocalStorage({
            email,
            version: latestItem.version,
            showNotification: true,
        });

        set({ showNotification: true, showBottomSheet: true });
    },

    dismissNotification: (email, version) => {
        if (!email) {
            return;
        }
        writeLocalStorage({ email, version, showNotification: false });
        set({ showNotification: false });
    },

    dismissBottomSheet: () => {
        set({ showBottomSheet: false });
    },
}));
